/**
 * InfinitSales - Conexao do Instagram (modo teste)
 *
 * Entrega 1A: conexao MANUAL, que funciona sem App Review da Meta. O admin
 * adiciona a conta do cliente como testadora no app, o cliente aceita o convite
 * pelo proprio Instagram, e o token gerado no painel e colado aqui.
 *
 * O fluxo embutido (Embedded Signup, um clique) e a Entrega 1B e depende da
 * aprovacao de instagram_business_manage_messages — semanas de fila. Ate la,
 * este e o unico caminho que destrava clientes reais.
 *
 * Requisito facil de esquecer, e que a doc da Meta repete: o app precisa estar
 * em modo LIVE para RECEBER webhooks. Publicar nao exige App Review. Sem isso
 * a conexao funciona, o envio funciona, e nenhuma mensagem chega — por isso o
 * aviso esta na tela e nao so na documentacao.
 */
import React, { useState } from 'react';
import { X, Instagram, Loader2, ExternalLink, AlertTriangle, KeyRound } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import instagramService from '../../services/instagram/instagramService';

const STEPS = [
  'No painel da Meta (App Dashboard), abra "App roles" e adicione a conta do cliente como Instagram tester.',
  'Peça ao cliente para aceitar o convite no Instagram: Configurações > Apps e sites > Convites de testador.',
  'Em "API setup with Instagram login", gere o token de acesso da conta.',
  'Cole o token abaixo.',
];

// Business Login (OAuth de producao). O ID e o "Instagram app ID" do produto
// Instagram no App Dashboard. Scopes: SO os que o produto usa — pedir scope a
// mais reprova o App Review.
const INSTAGRAM_APP_ID = process.env.REACT_APP_INSTAGRAM_APP_ID || '3762619197207196';
const OAUTH_SCOPES = 'instagram_business_basic,instagram_business_manage_messages';

const buildAuthorizeUrl = () => {
  // state anti-CSRF: gerado aqui, conferido na volta pelo shell do inbox.
  const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
  try { sessionStorage.setItem('ig_oauth_state', state); } catch { /* ignore */ }
  const params = new URLSearchParams({
    client_id: INSTAGRAM_APP_ID,
    redirect_uri: `${window.location.origin}/instagram`,
    response_type: 'code',
    scope: OAUTH_SCOPES,
    state,
    force_reauth: 'true',
  });
  return `https://www.instagram.com/oauth/authorize?${params.toString()}`;
};

const InstagramConnectionModal = ({ onClose, onConnected, externalError = null }) => {
  const { colors } = useTheme();
  const [token, setToken] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(externalError);
  const [showManual, setShowManual] = useState(false);

  const handleConnect = async (e) => {
    e?.preventDefault();
    const value = token.trim();
    if (!value || connecting) return;

    setConnecting(true);
    setError(null);
    try {
      const res = await instagramService.connectManual(value);
      onConnected?.(res?.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        (typeof detail === 'object' ? detail.message : detail)
        || 'Não foi possível conectar. Confira o token e tente de novo.'
      );
    } finally {
      setConnecting(false);
    }
  };

  const styles = {
    overlay: {
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    },
    modal: {
      backgroundColor: colors.bgPrimary, borderRadius: 12, width: '100%',
      maxWidth: 520, maxHeight: '90vh', overflowY: 'auto',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    },
    header: {
      display: 'flex', alignItems: 'center', gap: 10, padding: '18px 20px',
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    title: { flex: 1, fontSize: 17, fontWeight: 600, color: colors.textPrimary },
    body: { padding: 20, display: 'flex', flexDirection: 'column', gap: 16 },
    intro: { fontSize: 13, color: colors.textSecondary, lineHeight: 1.55 },
    steps: {
      margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8,
      fontSize: 13, color: colors.textPrimary, lineHeight: 1.5,
    },
    warning: {
      display: 'flex', gap: 10, padding: '12px 14px', borderRadius: 8,
      backgroundColor: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.35)',
      fontSize: 12.5, color: colors.textPrimary, lineHeight: 1.5,
    },
    label: { fontSize: 13, fontWeight: 600, color: colors.textPrimary },
    input: {
      width: '100%', boxSizing: 'border-box', padding: '11px 14px',
      borderRadius: 8, border: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.bgSecondary, color: colors.textPrimary,
      fontSize: 14, fontFamily: 'monospace', outline: 'none',
    },
    error: {
      padding: '10px 14px', borderRadius: 8, fontSize: 13,
      backgroundColor: 'rgba(239,68,68,0.12)', color: '#dc2626', lineHeight: 1.5,
    },
    footer: {
      display: 'flex', justifyContent: 'flex-end', gap: 10,
      padding: '16px 20px', borderTop: `1px solid ${colors.borderColor}`,
    },
    btnGhost: {
      padding: '10px 18px', borderRadius: 8, border: `1px solid ${colors.borderColor}`,
      backgroundColor: 'transparent', color: colors.textPrimary,
      fontSize: 14, cursor: 'pointer',
    },
    btnPrimary: {
      padding: '10px 20px', borderRadius: 8, border: 'none',
      backgroundColor: '#c13584', color: '#fff', fontSize: 14, fontWeight: 600,
      display: 'flex', alignItems: 'center', gap: 8,
    },
    oauthBtn: {
      width: '100%', padding: '13px 20px', borderRadius: 10, border: 'none',
      background: 'linear-gradient(90deg, #833ab4 0%, #c13584 55%, #e1306c 100%)',
      color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    },
    manualToggle: {
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: 'transparent', border: 'none', padding: 0,
      color: colors.textSecondary, fontSize: 12.5, cursor: 'pointer',
      textDecoration: 'underline', alignSelf: 'flex-start',
    },
    docLink: {
      display: 'inline-flex', alignItems: 'center', gap: 4,
      color: colors.accentPrimary, fontSize: 12.5, textDecoration: 'none',
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <Instagram size={22} color="#c13584" />
          <span style={styles.title}>Conectar Instagram</span>
          <X
            size={20}
            color={colors.textSecondary}
            style={{ cursor: 'pointer' }}
            onClick={onClose}
          />
        </div>

        <form onSubmit={handleConnect}>
          <div style={styles.body}>
            {/* Caminho de PRODUCAO: Business Login. O clique sai para o
                Instagram e a Meta volta para /instagram?code=..., que o shell
                do inbox troca pelo token no backend. */}
            <p style={styles.intro}>
              Entre com a conta profissional do Instagram para conectar o
              Direct ao InfinitSales. Você será levado ao Instagram para
              autorizar e voltará aqui automaticamente.
            </p>

            <button
              type="button"
              style={styles.oauthBtn}
              onClick={() => { window.location.href = buildAuthorizeUrl(); }}
            >
              <Instagram size={18} />
              Entrar com Instagram
            </button>

            {error && <div style={styles.error}>{error}</div>}

            <button
              type="button"
              style={styles.manualToggle}
              onClick={() => setShowManual((v) => !v)}
            >
              <KeyRound size={13} />
              {showManual
                ? 'Ocultar conexão por token (modo teste)'
                : 'Conectar com token (modo teste)'}
            </button>

            {showManual && (
              <>
                <p style={styles.intro}>
                  Enquanto a aprovação do aplicativo não sai, contas de teste
                  conectam pelo painel da Meta. São quatro passos:
                </p>

                <ol style={styles.steps}>
                  {STEPS.map((step) => <li key={step}>{step}</li>)}
                </ol>

                <div style={styles.warning}>
                  <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} color="#d97706" />
                  <span>
                    O aplicativo precisa estar no modo <strong>Live</strong> no painel
                    da Meta para receber mensagens. Publicar o aplicativo não exige
                    aprovação. Sem isso a conta conecta normalmente, mas nenhuma
                    mensagem chega ao inbox.
                  </span>
                </div>

                <div>
                  <label style={styles.label} htmlFor="ig-token">Token de acesso</label>
                  <input
                    id="ig-token"
                    style={{ ...styles.input, marginTop: 6 }}
                    placeholder="IGAAx..."
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <div style={{ marginTop: 8 }}>
                    <a
                      style={styles.docLink}
                      href="https://developers.facebook.com/apps/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Abrir o painel da Meta <ExternalLink size={12} />
                    </a>
                  </div>
                </div>
              </>
            )}
          </div>

          <div style={styles.footer}>
            <button type="button" style={styles.btnGhost} onClick={onClose}>
              Cancelar
            </button>
            {showManual && (
              <button
                type="submit"
                style={{
                  ...styles.btnPrimary,
                  opacity: token.trim() && !connecting ? 1 : 0.6,
                  cursor: token.trim() && !connecting ? 'pointer' : 'default',
                }}
                disabled={!token.trim() || connecting}
              >
                {connecting ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Conectando...</> : 'Conectar'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default InstagramConnectionModal;
